import { icon } from "../lib/icons";
import { scheduleHTML } from "./schedule";
import { dispatchHTML } from "./dispatch";
import { progressHTML } from "./progress";
import { bookingModalHTML } from "./booking";
import { feedbackHTML } from "./feedback";

const ROLES = ["Owner", "Scheduler", "Flight Instructor", "Student"] as const;

export function demoShellHTML(): string {
  return `
<div class="app">
  <header class="topbar">
    <span class="brand">
      <span class="brand-mark">${icon("plane", 13, 1.6, "white")}</span>
      Runway Sync<small>OPS</small>
    </span>
    <nav aria-label="Primary">
      <button type="button" :class="$store.app.page === 'schedule' ? 'active' : ''"
        :aria-current="$store.app.page === 'schedule' ? 'page' : null"
        @click="$store.app.go('schedule')">
        ${icon("calendar", 14)} Schedule
      </button>
      <button type="button" :class="$store.app.page === 'dispatch' ? 'active' : ''"
        :aria-current="$store.app.page === 'dispatch' ? 'page' : null"
        @click="$store.app.go('dispatch')">
        ${icon("tower", 14)} Dispatch
      </button>
      <button type="button" :class="$store.app.page === 'progress' ? 'active' : ''"
        :aria-current="$store.app.page === 'progress' ? 'page' : null"
        @click="$store.app.go('progress')">
        ${icon("grad", 14)} Progress
      </button>
    </nav>

    <div class="topbar-right">
      <span class="live-pulse"><span class="dot" aria-hidden="true"></span> Live<span x-show="$store.board.schoolSlug"> · <span x-text="($store.board.schoolSlug || '').toUpperCase()"></span></span></span>
      <span class="vsep" style="background: rgba(255,255,255,0.1)"></span>
      <span class="muted" style="font-size: 11.5px; color: var(--ink-400)">Viewing as</span>
      <div class="role-switcher" role="group" aria-label="Demo role">
        ${ROLES.map(r => `
          <button type="button" :class="$store.board.role === '${r}' ? 'active' : ''"
            :aria-pressed="$store.board.role === '${r}' ? 'true' : 'false'"
            @click="$store.board.setRole('${r}')">${r}</button>
        `).join("")}
      </div>
      <button type="button" class="btn btn-sm" @click="$store.ui.openFeedback()"
        style="background: rgba(255,255,255,0.06); color: white; border-color: rgba(255,255,255,0.12)">
        ${icon("send", 11)} Give feedback
      </button>
      <template x-if="$store.session.status === 'authed'">
        <button type="button" class="btn btn-sm btn-ghost" @click="$store.session.signOut()" style="color: var(--ink-400)">
          ${icon("logout", 12)} Sign out
        </button>
      </template>
      <template x-if="$store.session.status !== 'authed'">
        <span style="display: flex; gap: 6px">
          <button type="button" class="btn btn-sm btn-ghost" @click="$store.app.goLogin()" style="color: var(--ink-400)">
            Sign in
          </button>
          <button type="button" class="btn btn-sm btn-ghost" @click="$store.app.exitApp()" style="color: var(--ink-400)">
            ${icon("logout", 12)} Exit demo
          </button>
        </span>
      </template>
    </div>
  </header>

  ${demoBannerHTML()}

  <main class="content">
    <div class="page">
      ${pageHeaderHTML()}
      <template x-if="$store.board.loading && !$store.board.loaded">
        <div class="muted" style="padding: 32px; text-align: center">Loading…</div>
      </template>
      <template x-if="$store.board.error">
        <div class="bk-warn-list block" style="margin: 16px 0" role="alert" aria-live="assertive">
          <h2 class="bk-warn-h">${icon("warn", 13)} Could not load data</h2>
          <ul><li class="bk-warn-item"><span x-text="$store.board.error"></span></li></ul>
        </div>
      </template>
      <div x-show="$store.app.page === 'schedule' && $store.board.loaded">
        ${scheduleHTML()}
      </div>
      <div x-show="$store.app.page === 'dispatch' && $store.board.loaded">
        ${dispatchHTML()}
      </div>
      <div x-show="$store.app.page === 'progress' && $store.board.loaded">
        ${progressHTML()}
      </div>
    </div>
  </main>

  ${bookingModalHTML()}
  ${feedbackHTML()}
  ${undoToastHTML()}
</div>
`;
}

function undoToastHTML(): string {
  return `
    <template x-if="$store.ui.undo.visible">
      <div class="toast-stack" role="status" aria-live="polite">
        <div class="toast">
          <strong x-text="$store.ui.undo.message"></strong>
          <button type="button" class="btn btn-sm" @click="$store.ui.restoreUndo()">
            ${icon("arrowR", 11)} Undo
          </button>
          <button type="button" class="btn-icon btn btn-ghost" style="color: white; padding: 4px"
            aria-label="Dismiss" @click="$store.ui.dismissUndo()">${icon("x", 12)}</button>
          <span class="toast-progress"></span>
        </div>
      </div>
    </template>
  `;
}

function demoBannerHTML(): string {
  return `
    <div class="demo-banner" x-data="demoBanner()">
      <span class="role-tag" x-text="$store.board.role"></span>
      <strong x-text="currentName()"></strong>
      <span class="dim">·</span>
      <span x-text="message()"></span>
      <span class="dim mono" style="margin-left: auto; font-size: 11px" x-text="dateLabel"></span>
    </div>
  `;
}

function pageHeaderHTML(): string {
  return `
    <div class="page-h" x-show="$store.app.page === 'schedule'">
      <h1>Schedule board</h1>
      <span class="crumb">Today · 15 hr operating window · 3 aircraft</span>
    </div>
    <div class="page-h" x-show="$store.app.page === 'dispatch'">
      <h1>Dispatch board</h1>
      <span class="crumb">Real-time readiness · advance flights through the day</span>
    </div>
    <div class="page-h" x-show="$store.app.page === 'progress'">
      <h1>Student progress</h1>
      <span class="crumb">PPL track · stage check pacing</span>
    </div>
  `;
}
